/**
 * FDM Predictive Tab Component
 * AI predictions, alerts, and route risk profiles
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, BellRing, AlertTriangle, Sparkles, Zap, 
  RefreshCw, Route, CheckCircle2, Plane, Shield, AlertCircle
} from 'lucide-react';
import { getRiskLevelColor, getConfidenceColor, formatCategory } from './fdmUtils';

export default function FDMPredictiveTab({ 
  predictiveDashboard,
  predictions,
  predictiveAlerts,
  routeProfile,
  generatingPredictions,
  onGeneratePredictions,
  onShowRouteProfileDialog,
  onRefresh,
  onAcknowledgeAlert,
  onDismissAlert
}) {
  return (
    <div className="space-y-6 mt-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Active Predictions</p>
                <p className="text-2xl font-bold text-slate-100">{predictiveDashboard?.total_predictions || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20">
                <Brain className="w-5 h-5 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Active Alerts</p>
                <p className="text-2xl font-bold text-orange-400">{predictiveDashboard?.active_alerts || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/20">
                <BellRing className="w-5 h-5 text-orange-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">High Risk</p>
                <p className="text-2xl font-bold text-red-400">{predictiveDashboard?.high_risk_count || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/20">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">AI Status</p>
                <p className="text-lg font-bold text-green-400">
                  {predictiveDashboard?.ai_status?.enabled ? 'Online' : 'Pattern Only'}
                </p>
                {predictiveDashboard?.ai_status?.model && (
                  <p className="text-xs text-slate-500">{predictiveDashboard.ai_status.model}</p>
                )}
              </div>
              <div className="p-3 rounded-lg bg-green-500/20">
                <Sparkles className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3">
        <Button 
          onClick={onGeneratePredictions} 
          disabled={generatingPredictions}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="generate-predictions-btn"
        >
          {generatingPredictions ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Zap className="w-4 h-4 mr-2" />
          )}
          {generatingPredictions ? 'Analyzing...' : 'Generate AI Predictions'}
        </Button>
        <Button 
          variant="outline" 
          onClick={onShowRouteProfileDialog}
          data-testid="route-profile-btn"
        >
          <Route className="w-4 h-4 mr-2" />
          Route Risk Profile
        </Button>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Active Alerts Section */}
      {predictiveAlerts.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-slate-100 flex items-center gap-2">
                  <BellRing className="w-5 h-5 text-orange-400" />
                  Active Prediction Alerts
                </CardTitle>
                <CardDescription className="text-slate-400">AI-generated alerts requiring attention</CardDescription>
              </div>
              <Badge className="bg-orange-500/20 text-orange-400">{predictiveAlerts.length} Active</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {predictiveAlerts.slice(0, 5).map((alert) => (
                <div key={alert.id} className="p-4 rounded-lg bg-slate-900/50 border border-slate-700 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${alert.risk_level === 'critical' ? 'bg-red-500/20' : alert.risk_level === 'high' ? 'bg-orange-500/20' : 'bg-yellow-500/20'}`}>
                      <AlertTriangle className={`w-5 h-5 ${alert.risk_level === 'critical' ? 'text-red-400' : alert.risk_level === 'high' ? 'text-orange-400' : 'text-yellow-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-200">{alert.title}</p>
                      <p className="text-sm text-slate-400 mt-1">{alert.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={getRiskLevelColor(alert.risk_level)}>
                          {alert.risk_level?.toUpperCase()}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(alert.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => onAcknowledgeAlert(alert.id)}>
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Acknowledge
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => onDismissAlert(alert.id)}>
                      Dismiss
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Predictions List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-100 flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                AI Exceedance Predictions
              </CardTitle>
              <CardDescription className="text-slate-400">
                Predictions based on {predictiveDashboard?.patterns_summary?.flights_analyzed || 0} flights and {predictiveDashboard?.patterns_summary?.exceedances_analyzed || 0} historical exceedances
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {predictions.length === 0 ? (
            <div className="text-center py-12">
              <Brain className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No predictions generated yet</p>
              <p className="text-sm text-slate-500 mt-1">Click "Generate AI Predictions" to analyze patterns</p>
            </div>
          ) : (
            <div className="space-y-4">
              {predictions.map((pred) => (
                <div key={pred.id} className="p-4 rounded-lg bg-slate-900/50 border border-slate-700">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${pred.risk_level === 'critical' ? 'bg-red-500/20' : pred.risk_level === 'high' ? 'bg-orange-500/20' : pred.risk_level === 'moderate' ? 'bg-yellow-500/20' : 'bg-green-500/20'}`}>
                        {pred.category === 'aircraft_trend' ? (
                          <Plane className={`w-5 h-5 ${pred.risk_level === 'critical' ? 'text-red-400' : pred.risk_level === 'high' ? 'text-orange-400' : pred.risk_level === 'moderate' ? 'text-yellow-400' : 'text-green-400'}`} />
                        ) : pred.category === 'route_risk' ? (
                          <Route className={`w-5 h-5 ${pred.risk_level === 'critical' ? 'text-red-400' : pred.risk_level === 'high' ? 'text-orange-400' : pred.risk_level === 'moderate' ? 'text-yellow-400' : 'text-green-400'}`} />
                        ) : (
                          <AlertTriangle className={`w-5 h-5 ${pred.risk_level === 'critical' ? 'text-red-400' : pred.risk_level === 'high' ? 'text-orange-400' : pred.risk_level === 'moderate' ? 'text-yellow-400' : 'text-green-400'}`} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-200">{formatCategory(pred.category)}</p>
                          {pred.aircraft_registration && (
                            <Badge variant="outline" className="text-xs">{pred.aircraft_registration}</Badge>
                          )}
                          {pred.route && (
                            <Badge variant="outline" className="text-xs">{pred.route}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <Badge className={getRiskLevelColor(pred.risk_level)}>
                            {pred.risk_level?.toUpperCase()} RISK
                          </Badge>
                          <span className="text-sm text-slate-400">
                            Probability: <span className="font-medium text-slate-200">{pred.probability}%</span>
                          </span>
                          <span className={`text-sm ${getConfidenceColor(pred.confidence)}`}>
                            {pred.confidence?.replace('_', ' ')} confidence
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">{pred.prediction_window?.replace('_', ' ')}</p>
                      <p className="text-xs text-slate-500 mt-1">{pred.similar_past_incidents} past incidents</p>
                    </div>
                  </div>
                  
                  {/* Contributing Factors */}
                  {pred.contributing_factors?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <p className="text-xs text-slate-400 uppercase mb-2">Contributing Factors</p>
                      <div className="flex flex-wrap gap-2">
                        {pred.contributing_factors.slice(0, 3).map((factor, idx) => (
                          <span key={idx} className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300">{factor}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Recommended Actions */}
                  {pred.recommended_actions?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700">
                      <p className="text-xs text-slate-400 uppercase mb-2">Recommended Actions</p>
                      <ul className="space-y-1">
                        {pred.recommended_actions.slice(0, 3).map((action, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                            <Shield className="w-3 h-3 mt-1 text-blue-400 flex-shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Route Profile Result */}
      {routeProfile && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-slate-100 flex items-center gap-2">
                  <Route className="w-5 h-5 text-blue-400" />
                  Route Risk Profile: {routeProfile.route}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Based on {routeProfile.historical_flights_analyzed} historical flights
                </CardDescription>
              </div>
              <Badge className={getRiskLevelColor(routeProfile.risk_level)}>
                {routeProfile.risk_level?.toUpperCase()} RISK
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 rounded-lg bg-slate-900/50">
                <p className="text-xs text-slate-400">Overall Risk Score</p>
                <p className="text-2xl font-bold text-slate-100">{Math.round(routeProfile.overall_risk_score)}/100</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/50">
                <p className="text-xs text-slate-400">Historical Rate</p>
                <p className="text-2xl font-bold text-slate-100">{routeProfile.historical_exceedance_rate?.toFixed(1)}</p>
                <p className="text-xs text-slate-500">per 1000 flights</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-900/50">
                <p className="text-xs text-slate-400">Aircraft Type</p>
                <p className="text-lg font-medium text-slate-100">{routeProfile.aircraft_type || 'All Types'}</p>
              </div>
            </div>

            {routeProfile.crew_briefing_points?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-300 mb-2">Crew Briefing Points</p>
                <ul className="space-y-1">
                  {routeProfile.crew_briefing_points.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                      <AlertCircle className="w-4 h-4 mt-0.5 text-yellow-400 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {routeProfile.operational_recommendations?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-slate-300 mb-2">Operational Recommendations</p>
                <ul className="space-y-1">
                  {routeProfile.operational_recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                      <Shield className="w-4 h-4 mt-0.5 text-blue-400 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
